import { loadProgress, saveProgress } from '@/config/progress';
import type { Grade } from '@/config/scoring';
import { getSupabaseClient, isSupabaseConfigured, onAuthChange } from '@/services/supabaseClient';
import { ensureDiscordAccessToken, onDiscordAuthChange } from '@/discord';
import { captureError } from '@/sentry';
import { isDiscordActivity } from '@/utils/discordActivity';

const SINGLE_MODE = 'single';

type ApiScoreInput = {
  messageKey: string;
  highScore: number;
  highGrade: string;
  highAccuracy: number;
  completedAt: string | null;
};

type ApiSyncResponse = {
  scores: Array<{
    message_key: string;
    high_score: number;
    high_grade: string;
    high_accuracy: number;
    completed_at: string | null;
  }>;
};

async function getSupabaseAccessToken(): Promise<string | null> {
  if (!isSupabaseConfigured()) return null;
  const supabase = getSupabaseClient();
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

async function callUserScoresApi(
  action: 'sync' | 'save' | 'reset',
  gameMode: string,
  scores?: ApiScoreInput[],
): Promise<ApiSyncResponse | null> {
  const body: Record<string, unknown> = { action, gameMode };
  if (scores) body.scores = scores;

  // In Discord Activity mode, use Discord token (existing refresh system handles expiry)
  if (isDiscordActivity()) {
    const discordToken = await ensureDiscordAccessToken();
    console.log('[progress-sync] callUserScoresApi', { action, gameMode, hasDiscordToken: !!discordToken });
    if (!discordToken) return null;
    body.discordAccessToken = discordToken;
  } else {
    const accessToken = await getSupabaseAccessToken();
    console.log('[progress-sync] callUserScoresApi', { action, gameMode, hasToken: !!accessToken });
    if (!accessToken) return null;
    body.accessToken = accessToken;
  }

  try {
    const response = await fetch('/api/user-scores', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      console.warn('user-scores API failed:', response.status);
      return null;
    }

    return (await response.json()) as ApiSyncResponse;
  } catch (error) {
    console.warn('user-scores API error:', error);
    return null;
  }
}
const VALID_GRADES: Grade[] = ['SS', 'S', 'A', 'B', 'C', 'D'];

type UserScoreRow = {
  user_id: string;
  game_mode: string;
  message_key: string;
  high_score: number;
  high_grade: string;
  high_accuracy: number;
  completed_at: string | null;
  updated_at: string;
};

type UserScoreRecord = {
  game_mode: string;
  message_key: string;
  high_score: number;
  high_grade: string;
  high_accuracy: number;
  completed_at: string | null;
};

let hasSyncedOnce = false;

function parseTimestamp(value: string | null | undefined): number | null {
  if (!value) return null;
  const ts = Date.parse(value);
  return Number.isNaN(ts) ? null : ts;
}

function coerceGrade(value: unknown): Grade | '' {
  return VALID_GRADES.includes(value as Grade) ? (value as Grade) : '';
}

function earliestTimestamp(a: string | null | undefined, b: string | null | undefined): string | null {
  if (!a && !b) return null;
  if (!a) return b ?? null;
  if (!b) return a;
  const aTs = parseTimestamp(a);
  const bTs = parseTimestamp(b);
  if (aTs === null && bTs === null) return a;
  if (aTs === null) return b;
  if (bTs === null) return a;
  return aTs <= bTs ? a : b;
}

function timestampsEqual(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  const aTs = parseTimestamp(a);
  const bTs = parseTimestamp(b);
  if (aTs === null || bTs === null) return a === b;
  return aTs === bTs;
}

async function getSessionUserId(): Promise<string | null> {
  if (!isSupabaseConfigured()) return null;
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.auth.getSession();
  if (error) return null;
  return data.session?.user?.id ?? null;
}

function buildRow(userId: string, levelIndex: number, record: {
  highScore: number;
  highGrade: string;
  highAccuracy: number;
  completedAt: string | null;
}): UserScoreRow {
  return {
    user_id: userId,
    game_mode: SINGLE_MODE,
    message_key: `${SINGLE_MODE}:${levelIndex}`,
    high_score: record.highScore,
    high_grade: record.highGrade,
    high_accuracy: record.highAccuracy,
    completed_at: record.completedAt,
    updated_at: new Date().toISOString(),
  };
}

async function syncProgressViaApi(): Promise<boolean> {
  console.log('[progress-sync] syncProgressViaApi called');
  const result = await callUserScoresApi('sync', SINGLE_MODE);
  console.log('[progress-sync] sync API result', result);
  if (!result) return false;

  const byKey = new Map<string, UserScoreRecord>();
  for (const row of result.scores) {
    byKey.set(row.message_key, {
      game_mode: SINGLE_MODE,
      message_key: row.message_key,
      high_score: row.high_score,
      high_grade: row.high_grade,
      high_accuracy: row.high_accuracy,
      completed_at: row.completed_at,
    });
  }

  const progress = loadProgress();
  const updates: ApiScoreInput[] = [];
  let localChanged = false;

  progress.levels.forEach((level, index) => {
    const key = `${SINGLE_MODE}:${index}`;
    const db = byKey.get(key);

    let mergedScore = level.highScore;
    let mergedGrade = level.highGrade;
    let mergedAccuracy = level.highAccuracy;

    if (db && db.high_score > level.highScore) {
      mergedScore = db.high_score;
      mergedGrade = coerceGrade(db.high_grade);
      mergedAccuracy = typeof db.high_accuracy === 'number' ? db.high_accuracy : 0;
    }

    const mergedCompletedAt = earliestTimestamp(level.completedAt, db?.completed_at);

    if (mergedScore !== level.highScore
      || mergedGrade !== level.highGrade
      || mergedAccuracy !== level.highAccuracy
      || !timestampsEqual(mergedCompletedAt, level.completedAt)) {
      level.highScore = mergedScore;
      level.highGrade = mergedGrade;
      level.highAccuracy = mergedAccuracy;
      level.completedAt = mergedCompletedAt;
      localChanged = true;
    }

    const hasProgress = mergedScore > 0 || Boolean(mergedCompletedAt);

    if (!db) {
      if (hasProgress) {
        updates.push({
          messageKey: key,
          highScore: mergedScore,
          highGrade: mergedGrade,
          highAccuracy: mergedAccuracy,
          completedAt: mergedCompletedAt,
        });
      }
      return;
    }

    const needsUpsert = db.high_score !== mergedScore
      || db.high_grade !== mergedGrade
      || db.high_accuracy !== mergedAccuracy
      || !timestampsEqual(db.completed_at, mergedCompletedAt);

    if (needsUpsert && hasProgress) {
      updates.push({
        messageKey: key,
        highScore: mergedScore,
        highGrade: mergedGrade,
        highAccuracy: mergedAccuracy,
        completedAt: mergedCompletedAt,
      });
    }
  });

  if (localChanged) {
    saveProgress(progress);
  }

  if (updates.length > 0) {
    await callUserScoresApi('save', SINGLE_MODE, updates);
  }

  return true;
}

export async function syncProgressWithSupabase(): Promise<void> {
  if (!isSupabaseConfigured()) return;

  // Use API in Activity mode to avoid CSP issues
  if (isDiscordActivity()) {
    const success = await syncProgressViaApi();
    if (success) hasSyncedOnce = true;
    return;
  }

  const supabase = getSupabaseClient();
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !sessionData.session) {
    hasSyncedOnce = false;
    return;
  }

  const userId = sessionData.session.user.id;

  const { data: rows, error } = await supabase
    .from('user_scores')
    .select('game_mode,message_key,high_score,high_grade,high_accuracy,completed_at')
    .eq('game_mode', SINGLE_MODE);

  if (error) {
    console.warn('Failed to load remote progress:', error.message);
    return;
  }

  const byKey = new Map<string, UserScoreRecord>();
  for (const row of rows ?? []) {
    byKey.set(row.message_key, row as UserScoreRecord);
  }

  const progress = loadProgress();
  const updates: UserScoreRow[] = [];
  let localChanged = false;

  progress.levels.forEach((level, index) => {
    const key = `${SINGLE_MODE}:${index}`;
    const db = byKey.get(key);

    let mergedScore = level.highScore;
    let mergedGrade = level.highGrade;
    let mergedAccuracy = level.highAccuracy;

    if (db && db.high_score > level.highScore) {
      mergedScore = db.high_score;
      mergedGrade = coerceGrade(db.high_grade);
      mergedAccuracy = typeof db.high_accuracy === 'number' ? db.high_accuracy : 0;
    }

    const mergedCompletedAt = earliestTimestamp(level.completedAt, db?.completed_at);

    if (mergedScore !== level.highScore
      || mergedGrade !== level.highGrade
      || mergedAccuracy !== level.highAccuracy
      || !timestampsEqual(mergedCompletedAt, level.completedAt)) {
      level.highScore = mergedScore;
      level.highGrade = mergedGrade;
      level.highAccuracy = mergedAccuracy;
      level.completedAt = mergedCompletedAt;
      localChanged = true;
    }

    const hasProgress = mergedScore > 0 || Boolean(mergedCompletedAt);

    if (!db) {
      if (hasProgress) {
        updates.push(buildRow(userId, index, level));
      }
      return;
    }

    const needsUpsert = db.high_score !== mergedScore
      || db.high_grade !== mergedGrade
      || db.high_accuracy !== mergedAccuracy
      || !timestampsEqual(db.completed_at, mergedCompletedAt);

    if (needsUpsert && hasProgress) {
      updates.push(buildRow(userId, index, {
        highScore: mergedScore,
        highGrade: mergedGrade,
        highAccuracy: mergedAccuracy,
        completedAt: mergedCompletedAt,
      }));
    }
  });

  if (localChanged) {
    saveProgress(progress);
  }

  if (updates.length > 0) {
    const { error: upsertError } = await supabase
      .from('user_scores')
      .upsert(updates, { onConflict: 'user_id,game_mode,message_key' });
    if (upsertError) {
      console.warn('Failed to update remote progress:', upsertError.message);
      return;
    }
  }

  hasSyncedOnce = true;
}

export async function syncLevelProgressToSupabase(levelIndex: number): Promise<void> {
  if (!isSupabaseConfigured()) return;

  if (!hasSyncedOnce) {
    await syncProgressWithSupabase();
    if (!hasSyncedOnce) return;
  }

  const progress = loadProgress();
  const level = progress.levels[levelIndex];
  if (!level) return;
  if (level.highScore <= 0 && !level.completedAt) return;

  // Use API in Activity mode to avoid CSP issues
  if (isDiscordActivity()) {
    const key = `${SINGLE_MODE}:${levelIndex}`;
    await callUserScoresApi('save', SINGLE_MODE, [{
      messageKey: key,
      highScore: level.highScore,
      highGrade: level.highGrade,
      highAccuracy: level.highAccuracy,
      completedAt: level.completedAt,
    }]);
    return;
  }

  const userId = await getSessionUserId();
  if (!userId) {
    hasSyncedOnce = false;
    return;
  }

  const supabase = getSupabaseClient();
  const row = buildRow(userId, levelIndex, level);
  const { error } = await supabase
    .from('user_scores')
    .upsert(row, { onConflict: 'user_id,game_mode,message_key' });

  if (error) {
    console.warn('Failed to sync progress row:', error.message);
  }
}

export function initProgressSync(): void {
  if (!isSupabaseConfigured()) return;
  void syncProgressWithSupabase();
  onAuthChange(() => {
    void syncProgressWithSupabase();
  });
  // In Discord Activity mode, also sync when Discord auth completes
  onDiscordAuthChange(() => {
    void syncProgressWithSupabase();
  });
}

export async function resetRemoteProgress(gameMode: string = SINGLE_MODE): Promise<void> {
  if (!isSupabaseConfigured()) return;

  // Use API in Activity mode to avoid CSP issues
  if (isDiscordActivity()) {
    await callUserScoresApi('reset', gameMode);
    return;
  }

  const userId = await getSessionUserId();
  if (!userId) return;

  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from('user_scores')
    .delete()
    .eq('user_id', userId)
    .eq('game_mode', gameMode);

  if (error) {
    const err = new Error(`Failed to reset remote progress: ${error.message}`);
    captureError(err, { gameMode, userId });
    throw err;
  }
}
